import { useMemo, useState } from "react";
import { CheckCircle, Clock, Copy, Plus, Trash } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import TimeSelect from "@/components/TimeSelect";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const DAY_LABELS = {
  Mon: "Monday",
  Tue: "Tuesday",
  Wed: "Wednesday",
  Thu: "Thursday",
  Fri: "Friday",
  Sat: "Saturday",
  Sun: "Sunday",
};

function minutesFromTime(time) {
  const [h, m] = String(time || "00:00").split(":").map((v) => Number(v));
  if (!Number.isFinite(h) || !Number.isFinite(m)) return 0;
  return h * 60 + m;
}

function timeFromMinutes(total) {
  const mins = ((total % 1440) + 1440) % 1440;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function formatTime(time) {
  const mins = minutesFromTime(time);
  const hour = Math.floor(mins / 60);
  const minute = mins % 60;
  const suffix = hour >= 12 ? "PM" : "AM";
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${String(minute).padStart(2, "0")} ${suffix}`;
}

function slotsForSession(session, duration) {
  const start = minutesFromTime(session.start_time);
  const end = minutesFromTime(session.end_time);
  if (end <= start || duration <= 0) return [];
  const slots = [];
  for (let current = start; current + duration <= end && slots.length < 48; current += duration) {
    slots.push(timeFromMinutes(current));
  }
  return slots;
}

function defaultSession(index = 0) {
  return {
    id: `session_${Date.now()}_${index}`,
    label: index === 0 ? "Morning" : "Evening",
    start_time: index === 0 ? "09:00" : "17:00",
    end_time: index === 0 ? "13:00" : "20:00",
    max_appointments: 0,
  };
}

export default function SlotScheduleDesigner({
  schedule,
  onChange,
  slotDuration = 30,
  onSlotDurationChange,
}) {
  const [selectedDay, setSelectedDay] = useState(DAYS.find((day) => schedule?.[day]?.enabled) || "Mon");
  const selected = useMemo(
    () => schedule?.[selectedDay] || { enabled: false, sessions: [] },
    [schedule, selectedDay],
  );
  const sessions = useMemo(
    () => (
      selected.sessions?.length
        ? selected.sessions
        : [{ id: "default", label: "Clinic", start_time: selected.start_time || "09:00", end_time: selected.end_time || "19:00", max_appointments: 0 }]
    ),
    [selected],
  );

  const totalSlots = useMemo(
    () => sessions.reduce((sum, session) => sum + slotsForSession(session, Number(slotDuration) || 30).length, 0),
    [sessions, slotDuration],
  );

  const updateDay = (day, patch) => {
    const current = schedule?.[day] || { enabled: false, sessions: [] };
    onChange(day, { ...current, ...patch });
  };

  const updateSession = (index, patch) => {
    const nextSessions = sessions.map((session, i) => (i === index ? { ...session, ...patch } : session));
    updateDay(selectedDay, {
      enabled: true,
      sessions: nextSessions,
      start_time: nextSessions[0]?.start_time || "09:00",
      end_time: nextSessions[nextSessions.length - 1]?.end_time || "19:00",
    });
  };

  const addSession = () => {
    const nextSessions = [...sessions, defaultSession(sessions.length)];
    updateDay(selectedDay, {
      enabled: true,
      sessions: nextSessions,
      start_time: nextSessions[0].start_time,
      end_time: nextSessions[nextSessions.length - 1].end_time,
    });
  };

  const removeSession = (index) => {
    const nextSessions = sessions.filter((_, i) => i !== index);
    const safeSessions = nextSessions.length ? nextSessions : [defaultSession(0)];
    updateDay(selectedDay, {
      sessions: safeSessions,
      start_time: safeSessions[0].start_time,
      end_time: safeSessions[safeSessions.length - 1].end_time,
    });
  };

  const copyToWeekdays = () => {
    DAYS.filter((day) => day !== "Sun").forEach((day) => {
      updateDay(day, {
        ...selected,
        enabled: true,
        sessions: sessions.map((session, index) => ({ ...session, id: index === 0 ? "default" : `session_${index + 1}` })),
      });
    });
  };

  return (
    <div className="space-y-5">
      <div className="rounded-2xl border border-border bg-background/60 p-4 sm:p-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h3 className="font-display text-xl font-bold tracking-tight text-foreground">Weekly schedule</h3>
            <p className="mt-1 text-sm text-muted-foreground">Set clinic ranges and preview how appointments will be spaced through the day.</p>
          </div>
          <div className="space-y-2">
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">Visit interval</div>
            <div className="inline-flex rounded-xl border border-border bg-muted/40 p-1">
              {[15, 30, 45, 60].map((duration) => (
                <button
                  key={duration}
                  type="button"
                  onClick={() => onSlotDurationChange?.(duration)}
                  className={`rounded-lg px-3 py-1.5 text-xs font-bold transition-all ${
                    Number(slotDuration) === duration
                      ? "bg-card text-primary shadow-sm ring-1 ring-border"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {duration} min
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
        <div className="space-y-2 lg:col-span-3">
          <div className="px-1 text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">Select day</div>
          {DAYS.map((day) => {
            const enabled = !!schedule?.[day]?.enabled;
            const active = selectedDay === day;
            return (
              <button
                key={day}
                type="button"
                onClick={() => setSelectedDay(day)}
                className={`flex w-full items-center justify-between rounded-xl border px-4 py-3 text-left transition-all ${
                  active
                    ? "border-primary bg-primary text-primary-foreground shadow-sm"
                    : "border-border bg-card text-muted-foreground hover:border-primary/40 hover:text-foreground"
                }`}
              >
                <span className="font-bold">{DAY_LABELS[day]}</span>
                {active ? (
                  <CheckCircle size={18} weight="duotone" />
                ) : enabled ? (
                  <span className="h-2 w-2 rounded-full bg-primary" />
                ) : (
                  <span className="text-[10px] font-bold uppercase opacity-60">Off</span>
                )}
              </button>
            );
          })}
        </div>

        <div className="space-y-5 lg:col-span-9">
          <div className="overflow-hidden rounded-2xl border border-border bg-card">
            <div className="flex flex-col gap-3 border-b border-border bg-muted/30 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
              <div className="flex items-center gap-2 text-foreground">
                <Clock size={20} weight="duotone" className="text-primary" />
                <h3 className="font-display text-lg font-bold">Availability ranges: {DAY_LABELS[selectedDay]}</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => updateDay(selectedDay, { enabled: !selected.enabled })}
                  className={`rounded-full px-4 py-2 text-xs font-bold transition-all ${
                    selected.enabled ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
                  }`}
                >
                  {selected.enabled ? "Open" : "Closed"}
                </button>
                <button
                  type="button"
                  onClick={copyToWeekdays}
                  className="inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-xs font-bold text-primary hover:bg-primary/5"
                >
                  <Copy size={14} weight="duotone" /> Copy weekdays
                </button>
              </div>
            </div>

            <div className="space-y-4 p-4 sm:p-5">
              {selected.enabled ? (
                <>
                  {sessions.map((session, index) => (
                    <div key={`${session.id}-${index}`} className="rounded-2xl border border-border bg-background p-4">
                      <div className="grid gap-3 md:grid-cols-[1.2fr_1fr_1fr_0.8fr_auto] md:items-end">
                        <FieldLabel label="Session">
                          <Input value={session.label} onChange={(e) => updateSession(index, { label: e.target.value })} className="h-10 bg-card" />
                        </FieldLabel>
                        <FieldLabel label="From">
                          <TimeSelect value={session.start_time} onChange={(value) => updateSession(index, { start_time: value })} />
                        </FieldLabel>
                        <FieldLabel label="To">
                          <TimeSelect value={session.end_time} onChange={(value) => updateSession(index, { end_time: value })} />
                        </FieldLabel>
                        <FieldLabel label="Max">
                          <Input
                            type="number"
                            min={0}
                            value={session.max_appointments || ""}
                            onChange={(e) => updateSession(index, { max_appointments: Number(e.target.value) || 0 })}
                            placeholder="No cap"
                            className="h-10 bg-card"
                          />
                        </FieldLabel>
                        {sessions.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeSession(index)}
                            className="inline-flex h-10 items-center justify-center rounded-xl border border-destructive/20 px-3 text-destructive hover:bg-destructive/10"
                            aria-label="Remove session"
                          >
                            <Trash size={18} weight="duotone" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addSession}
                    className="flex w-full flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-primary/40 py-6 text-primary transition-all hover:bg-primary/5"
                  >
                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                      <Plus size={20} weight="bold" />
                    </span>
                    <span className="text-xs font-bold uppercase tracking-[0.16em]">Add time range</span>
                  </button>
                </>
              ) : (
                <div className="rounded-2xl border border-dashed border-border bg-muted/20 px-4 py-10 text-center text-sm text-muted-foreground">
                  {DAY_LABELS[selectedDay]} is closed. Turn it open to add clinic timings.
                </div>
              )}
            </div>
          </div>

          {selected.enabled && (
            <div className="overflow-hidden rounded-2xl border border-border bg-card">
              <div className="border-b border-border bg-muted/30 px-4 py-4 sm:px-5">
                <h3 className="font-display text-lg font-bold text-foreground">Generated preview ({totalSlots})</h3>
              </div>
              <div className="space-y-6 p-4 sm:p-5">
                {sessions.map((session, index) => {
                  const slots = slotsForSession(session, Number(slotDuration) || 30);
                  return (
                    <div key={`${session.id}-preview-${index}`} className="space-y-3">
                      <div className="flex items-center gap-3">
                        <span className="whitespace-nowrap text-xs font-bold text-muted-foreground">
                          {session.label || "Clinic"} · {formatTime(session.start_time)} - {formatTime(session.end_time)}
                        </span>
                        <span className="h-px flex-1 bg-border" />
                      </div>
                      {slots.length ? (
                        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
                          {slots.map((slot) => (
                            <div key={slot} className="rounded-xl border border-border bg-background px-3 py-2 text-center">
                              <div className="text-sm font-bold text-foreground">{formatTime(slot)}</div>
                              <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">{slotDuration} min</div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="rounded-xl border border-dashed border-border px-4 py-5 text-sm text-muted-foreground">
                          End time must be after start time to preview appointments.
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function FieldLabel({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}
